import { Suspense, useEffect } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import { ThemeProvider } from '@/lib/theme';
import { CurrencyProvider } from '@/lib/currency';
import { queryClient } from '@/lib/queryClient';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { routes } from '@/routes';
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary';
import {
  isChunkLoadError,
  recoverFromChunkError,
} from '@/components/common/ErrorScreen';

import '@/lib/i18n';

const router = createBrowserRouter(routes);

/** Last-resort handler for chunk-load errors that escape every React boundary
 *  (e.g. a Suspense fallback firing while a stale chunk URL is fetched).
 *  `recoverFromChunkError` is throttled, so this won't hot-loop. */
function useGlobalChunkRecovery() {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      if (isChunkLoadError(e.error ?? e.message)) recoverFromChunkError();
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      if (isChunkLoadError(e.reason)) recoverFromChunkError();
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);
}

export function App() {
  useGlobalChunkRecovery();
  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <CurrencyProvider>
            <TooltipProvider delayDuration={150}>
              <Suspense fallback={null}>
                <RouterProvider router={router} />
              </Suspense>
              <Toaster />
            </TooltipProvider>
          </CurrencyProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}
